import React from 'react';
import { SignIn, SignUp } from '@clerk/clerk-react';
import { ArrowLeft, CheckCircle, ShieldCheck, Sparkle } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import './AuthPage.css';

interface AuthPageProps {
  mode: 'login' | 'signup';
  clerkEnabled: boolean;
}

const AuthPage: React.FC<AuthPageProps> = ({ mode, clerkEnabled }) => {
  const isLogin = mode === 'login';

  if (!clerkEnabled) {
    return (
      <main className="auth-page">
        <section className="auth-shell auth-shell-disabled">
          <h1>Authentication is disabled in this environment</h1>
          <p>
            Add Clerk keys to enable sign in and account-based credits. You can still open the app
            workspace in local mode.
          </p>
          <Link to="/app" className="auth-back-link">
            <ArrowLeft size={14} aria-hidden="true" /> Open App
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <section className="auth-shell" aria-label="Sign in and account explanation">
        <aside className="auth-side-panel">
          <Link to="/" className="auth-back-link">
            <ArrowLeft size={14} aria-hidden="true" /> Back to home
          </Link>

          <p className="auth-kicker">Agency workspace access</p>
          <h1>{isLogin ? 'Welcome back to VidInsight' : 'Create your VidInsight workspace'}</h1>
          <p>
            Sign in once and your credits, analysis history, and case-study outputs are saved
            automatically.
          </p>

          <ul>
            <li>
              <CheckCircle size={16} weight="duotone" aria-hidden="true" />
              <span>Owner credits are ready immediately after sign in.</span>
            </li>
            <li>
              <Sparkle size={16} weight="duotone" aria-hidden="true" />
              <span>BYOK is optional and only needed for higher volume usage.</span>
            </li>
            <li>
              <ShieldCheck size={16} weight="duotone" aria-hidden="true" />
              <span>Bot checks and account checks protect your billing and API usage.</span>
            </li>
          </ul>
        </aside>

        <div className="auth-widget-pane">
          {isLogin ? (
            <SignIn
              routing="path"
              path="/login"
              signUpUrl="/signup"
              forceRedirectUrl="/app"
              fallbackRedirectUrl="/app"
              appearance={{
                variables: {
                  colorPrimary: '#a25826',
                  colorText: '#201915',
                  colorInputText: '#201915',
                  colorBackground: '#fffdfa',
                  colorInputBackground: '#fffaf2',
                  colorBorder: '#dcc9b8',
                  borderRadius: '12px',
                  fontFamily: 'Outfit, Segoe UI, sans-serif',
                },
              }}
            />
          ) : (
            <SignUp
              routing="path"
              path="/signup"
              signInUrl="/login"
              forceRedirectUrl="/app"
              fallbackRedirectUrl="/app"
              appearance={{
                variables: {
                  colorPrimary: '#a25826',
                  colorText: '#201915',
                  colorInputText: '#201915',
                  colorBackground: '#fffdfa',
                  colorInputBackground: '#fffaf2',
                  colorBorder: '#dcc9b8',
                  borderRadius: '12px',
                  fontFamily: 'Outfit, Segoe UI, sans-serif',
                },
              }}
            />
          )}
        </div>
      </section>
    </main>
  );
};

export default AuthPage;
